import { useTranslation } from 'react-i18next';
import { Chapter } from './AudioSegmentManager';
import { Controller, useForm } from 'react-hook-form';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Typography } from '@mui/material';
import { FormTinyMCEInput } from '@wajeez-web/components/form-builder';

interface ChapterForm {
  title: string;
  text: string;
}

interface EditChapterModalProps {
  chapter: Chapter;
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  onSave: (updatedChapter: Chapter) => void;
  isRtl: boolean;
  onDelete: (id: number | undefined) => void;
}
export const EditChapterModal = ({
  chapter,
  isOpen,
  onOpen,
  onClose,
  onSave,
  isRtl,
  onDelete,
}: EditChapterModalProps) => {
  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ChapterForm>({
    mode: 'all',
    defaultValues: { title: chapter.title, text: chapter.text },
  });

  const onSubmit = (data: ChapterForm) => {
    const updatedChapter: Chapter = {
      ...chapter,
      title: data.title.trim(),
      text: data.text,
    };
    onSave(updatedChapter);
    reset();
    onClose();
  };

  return (
    <div
      className="modal flex justify-center items-start gap-4 w-max "
      onClick={(e) => e.stopPropagation()}
    >
      <div
        className="bg-white border p-2 rounded-lg text-[12px] font-bold z-10 flex justify-center items-center gap-2 cursor-pointer hover:scale-105"
        onClick={isOpen ? onClose : onOpen}
      >
        <div>{chapter.title}</div>
        {isOpen ? <ChevronRight /> : <ChevronLeft />}
      </div>
      {isOpen && (
        <form
          className="bg-white border p-5 rounded-lg text-[12px] font-bold z-10 flex flex-col gap-3 mt-[-15px]"
          onSubmit={handleSubmit(onSubmit)}
        >
          <Typography variant="h6">{t('inputs.labels.Title')}</Typography>
          <Controller
            name="title"
            control={control}
            rules={{
              required:
                t('inputs.messages.Required') ?? 'This field is required',
            }}
            render={({ field }) => (
              <input
                {...field}
                className={`border text-gray-900 text-sm rounded-lg block w-full p-2.5 ${
                  errors.title
                    ? 'border-red-600 focus:border-red-600 focus:ring-red-600'
                    : 'border-gray-300 focus:border-blue-500'
                }`}
                placeholder={
                  t('inputs.placeholders.Enter chapter title') ??
                  'Enter chapter title'
                }
              />
            )}
          />
          {errors.title && (
            <div className="text-red-600 text-[12px] font-medium">
              {errors.title.message}
            </div>
          )}

          <FormTinyMCEInput
            isRtl={isRtl}
            control={control}
            name="text"
            fieldType={{
              fieldType: 2,
              fieldOptions: {},
              label: t('inputs.labels.Text') ?? 'Text',
            }}
          />

          <div className="flex justify-around items-center w-full mt-3">
            <button
              type="submit"
              className="bg-blue-400 p-3 rounded-lg text-[13px] font-medium text-white"
            >
              {t('buttons.Save')}
            </button>
            <button
              type="button"
              className="bg-red-500 p-3 rounded-lg text-[13px] font-medium text-white"
              onClick={() => {
                onDelete(chapter.id);
                onClose();
              }}
            >
              {t('buttons.Delete')}
            </button>
            <button
              type="button"
              className="bg-red-500 p-3 rounded-lg text-[13px] font-medium text-white"
              onClick={() => {
                reset({ title: chapter.title, text: chapter.text });
                onClose();
              }}
            >
              {t('buttons.Cancel')}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
